import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Plus, Search, Loader2, ArrowLeft, Upload, Sparkles,
  Copy, ExternalLink, Trash2, Eye, EyeOff, Check,
  ChevronRight, Image as ImageIcon, Moon, RefreshCcw, PlayCircle,
  Film, Globe, Sofa,
} from 'lucide-react';
import {
  ShareDemo, ShareDemoStatus, ShareDemoDownloadMode, ShareDemoCtaMode,
  ShareDemoWorkflowType, ShareDemoSceneType,
} from '../types';
import {
  supabase,
  getShareDemos, createShareDemo, updateShareDemo, deleteShareDemo,
  uploadShareDemoImage, generateUniqueSlug, getShareDemoAnalytics,
  getThumbnailUrl, uploadShareDemoVideo,
  createHyperDuskShareProject, generateHyperDuskSharePreview,
  approveHyperDuskShareVariant, generateHyperDuskShareAnimation,
  pollHyperDuskShareStatus, fetchHyperDuskShareProject,
  copyHyperDuskAssetToShareDemo, uploadHyperDuskImage,
} from '../services/supabase';
import { declutterImage } from '../services/gemini';
import { creditsForOperation, fetchPhotoclearPrices, finalizePhotoclearAction, releasePhotoclearAction, reservePhotoclearAction } from '../services/photoclearBilling';
import { formatPhotoclearCreditLabel, quotePhotoclearOperation } from '@rehub/pricing';
import {
  AUTO_DECLUTTER_PROMPT, CARS_ONLY_PROMPT, BINS_ONLY_PROMPT, GARDEN_DECLUTTER_PROMPT,
} from '../constants';
import {
  HYPER_DUSK_PRESETS, HYPER_DUSK_ANIMATION_STYLES, HYPER_DUSK_SCENE_OPTIONS,
  HYPER_DUSK_DURATION_OPTIONS, DEFAULT_HYPER_DUSK_PRESET_ID,
  DEFAULT_HYPER_DUSK_ANIMATION_STYLE, DEFAULT_HYPER_DUSK_DURATION,
  type HyperDuskAnimationStyle, type HyperDuskProjectImageLite,
  type HyperDuskProjectLite,
} from '../hyperdusk-shared';
import ShareDemoAnalytics from './ShareDemoAnalytics';
import BeforeAfterSlider from './BeforeAfterSlider';
import { SHARE_DEMO_APPS, getShareDemoUrl, getShareDemoHost } from '../shareDemoApps';

interface ShareDemoManagerProps {
  userId: string;
}

type View = 'list' | 'create' | 'edit';

const STATUS_COLORS: Record<ShareDemoStatus, string> = {
  draft: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  published: 'bg-green-500/10 text-green-400 border-green-500/20',
  unpublished: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
};

// Photoclear processing modes available when building a demo image.
const PHOTO_MODES = [
  { id: 'declutter', label: 'Declutter', prompt: AUTO_DECLUTTER_PROMPT },
  { id: 'car', label: 'Remove car', prompt: CARS_ONLY_PROMPT },
  { id: 'bins', label: 'Remove bins', prompt: BINS_ONLY_PROMPT },
  { id: 'garden', label: 'Tidy garden', prompt: GARDEN_DECLUTTER_PROMPT },
] as const;

type PhotoMode = (typeof PHOTO_MODES)[number]['id'];

const DEMO_OPERATION: Record<PhotoMode, string> = {
  declutter: 'photoclear_auto',
  car: 'photoclear_cars_only',
  bins: 'photoclear_bins_only',
  garden: 'photoclear_garden_declutter',
};

// Signup CTA destinations the admin can quick-pick from.
const SIGNUP_PRESETS = [
  { id: 'photoclear', label: 'Photoclear', url: 'https://photoclear.rehub.studio/signup' },
  { id: 'rehub', label: 'ReHub', url: 'https://hub.rehub.studio' },
] as const;

// Saved "Book a Demo" links per agent — browser-persisted admin convenience so
// an agent's booking URL / name can be reused across demos without retyping.
const AGENT_LINKS_KEY = 'photoclear_share_demo_agent_demo_links';

interface SavedAgentLink {
  id: string;
  label: string;
  demoUrl: string;
  agencyName?: string;
}

const loadAgentLinks = (): SavedAgentLink[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(AGENT_LINKS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const persistAgentLinks = (links: SavedAgentLink[]) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(AGENT_LINKS_KEY, JSON.stringify(links));
  } catch {
    /* ignore quota / serialization errors */
  }
};

// Aspect-ratio padding identical to App.tsx prepareImageForAPI
const prepareForGemini = (file: File): Promise<{ imageData: string; aspectRatio: string; restoreRect: { x: number; y: number; width: number; height: number } }> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const w = img.width, h = img.height, ratio = w / h;
      const supported = [
        { id: '1:1', val: 1 }, { id: '3:4', val: 3 / 4 }, { id: '4:3', val: 4 / 3 },
        { id: '9:16', val: 9 / 16 }, { id: '16:9', val: 16 / 9 }, { id: '21:9', val: 21 / 9 },
      ];
      const best = supported.reduce((p, c) => Math.abs(c.val - ratio) < Math.abs(p.val - ratio) ? c : p);
      let pw: number, ph: number, ox: number, oy: number;
      if (ratio > best.val) { pw = w; ph = w / best.val; ox = 0; oy = (ph - h) / 2; }
      else { ph = h; pw = h * best.val; ox = (pw - w) / 2; oy = 0; }
      const canvas = document.createElement('canvas');
      canvas.width = pw; canvas.height = ph;
      const ctx = canvas.getContext('2d')!;
      ctx.imageSmoothingEnabled = false;
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, pw, ph);
      ctx.drawImage(img, ox, oy, w, h);
      resolve({
        imageData: canvas.toDataURL('image/png'),
        aspectRatio: best.id,
        restoreRect: { x: ox / pw, y: oy / ph, width: w / pw, height: h / ph },
      });
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

// Grab a poster frame from a locally-selected video so the manual
// "Upload Video" workflow can satisfy original_image_url / processed_image_url
// (and the public page poster) without a separate image upload.
const captureVideoPoster = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    (video as any).playsInline = true;
    const url = URL.createObjectURL(file);
    const cleanup = () => URL.revokeObjectURL(url);
    let seeked = false;

    const grab = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 1280;
        canvas.height = video.videoHeight || 720;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        cleanup();
        resolve(canvas.toDataURL('image/jpeg', 0.9));
      } catch (err) {
        cleanup();
        reject(err);
      }
    };

    video.onloadeddata = () => {
      // Seek slightly past the start to avoid a black opening frame.
      try { video.currentTime = Math.min(0.1, (video.duration || 1) / 2); }
      catch { grab(); }
    };
    video.onseeked = () => { if (!seeked) { seeked = true; grab(); } };
    video.onerror = () => { cleanup(); reject(new Error('Could not read this video to build a poster image.')); };
    video.src = url;
  });

const cropResult = (base64: string, rect: { x: number; y: number; width: number; height: number }): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const tw = Math.round(img.width * rect.width);
      const th = Math.round(img.height * rect.height);
      const sx = Math.round(img.width * rect.x);
      const sy = Math.round(img.height * rect.y);
      const c = document.createElement('canvas');
      c.width = tw; c.height = th;
      const ctx = c.getContext('2d')!;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, sx, sy, tw, th, 0, 0, tw, th);
      resolve(c.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = base64;
  });

// Fallback cover used for 360-tour demos when the user doesn't upload one.
// Keeps original/processed_image_url populated so the share page (og:image,
// not-found gate) and list thumbnail keep working without a manual upload.
const DEFAULT_TOUR_COVER_URL = 'https://360tours.rehub.studio/360tours-studio-logo-transparent.png';

export default function ShareDemoManager({ userId }: ShareDemoManagerProps) {
  const [view, setView] = useState<View>('list');
  const [demos, setDemos] = useState<ShareDemo[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Edit/create state
  const [editingDemo, setEditingDemo] = useState<ShareDemo | null>(null);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);

  // Form fields
  const [internalName, setInternalName] = useState('');
  const [workflowType, setWorkflowType] = useState<ShareDemoWorkflowType>('photoclear');
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [processedFile, setProcessedFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [processedPreview, setProcessedPreview] = useState<string | null>(null);
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);
  const [processedUrl, setProcessedUrl] = useState<string | null>(null);
  const [photoMode, setPhotoMode] = useState<PhotoMode>('declutter');
  const [processedVideoUrl, setProcessedVideoUrl] = useState<string | null>(null);
  // Manual "upload video" workflow
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [slug, setSlug] = useState('');
  const [headline, setHeadline] = useState("Here's how one of your listings could look with Photoclear");
  const [subheadline, setSubheadline] = useState('A quick before-and-after example showing how clutter can be removed and presentation improved in seconds.');
  const [tourUrl, setTourUrl] = useState('');
  const [agencyName, setAgencyName] = useState('');
  const [agencyLogoUrl, setAgencyLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [sourceLabel, setSourceLabel] = useState('');
  const [ctaMode, setCtaMode] = useState<ShareDemoCtaMode>('signup');
  const [ctaSignupUrl, setCtaSignupUrl] = useState('https://photoclear.rehub.studio/signup');
  const [ctaDemoUrl, setCtaDemoUrl] = useState('https://calendly.com/rehub');
  const [downloadMode, setDownloadMode] = useState<ShareDemoDownloadMode>('free');
  const [watermarkEnabled, setWatermarkEnabled] = useState(false);
  const [brandingEnabled, setBrandingEnabled] = useState(true);
  const [adminNotes, setAdminNotes] = useState('');
  const [listingUrl, setListingUrl] = useState('');
  const [appsUsed, setAppsUsed] = useState<string[]>([]);
  const [status, setStatus] = useState<ShareDemoStatus>('draft');

  // Saved agent listing links (browser-persisted)
  const [agentLinks, setAgentLinks] = useState<SavedAgentLink[]>(() => loadAgentLinks());

  const saveCurrentAgentLink = useCallback(() => {
    const url = ctaDemoUrl.trim();
    if (!url) return;
    const label = (agencyName.trim() || url).slice(0, 80);
    setAgentLinks((prev) => {
      // De-dupe by URL: update the label if it already exists, else prepend.
      const existing = prev.find((l) => l.demoUrl === url);
      const next = existing
        ? prev.map((l) => (l.demoUrl === url ? { ...l, label, agencyName: agencyName.trim() || undefined } : l))
        : [{ id: `${Date.now()}`, label, demoUrl: url, agencyName: agencyName.trim() || undefined }, ...prev];
      persistAgentLinks(next);
      return next;
    });
  }, [ctaDemoUrl, agencyName]);

  const applyAgentLink = useCallback((id: string) => {
    const link = agentLinks.find((l) => l.id === id);
    if (!link) return;
    setCtaDemoUrl(link.demoUrl);
    if (link.agencyName) setAgencyName(link.agencyName);
  }, [agentLinks]);

  const deleteAgentLink = useCallback((id: string) => {
    setAgentLinks((prev) => {
      const next = prev.filter((l) => l.id !== id);
      persistAgentLinks(next);
      return next;
    });
  }, []);

  const handleAgencyLogoSelect = async (file: File | undefined) => {
    if (!file || !file.type.startsWith('image/') || !editingDemo) return;
    setUploadingLogo(true);
    try {
      const url = await uploadShareDemoImage(file, editingDemo.id, 'agency_logo');
      if (url) {
        // Cache-bust so a replaced logo (same path) refreshes in the preview.
        const busted = `${url}?v=${Date.now()}`;
        setAgencyLogoUrl(busted);
        await updateShareDemo(editingDemo.id, { agency_logo_url: busted } as any);
      }
    } catch (err: any) {
      alert('Logo upload failed: ' + (err.message || 'Unknown error'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeAgencyLogo = async () => {
    setAgencyLogoUrl(null);
    if (editingDemo) await updateShareDemo(editingDemo.id, { agency_logo_url: null } as any);
  };

  // Hyper Dusk-specific state
  const [hdSceneType, setHdSceneType] = useState<ShareDemoSceneType>('exterior');
  const [hdPresetId, setHdPresetId] = useState<string>(DEFAULT_HYPER_DUSK_PRESET_ID);
  const [hdAnimationStyle, setHdAnimationStyle] = useState<HyperDuskAnimationStyle>(DEFAULT_HYPER_DUSK_ANIMATION_STYLE);
  const [hdDuration, setHdDuration] = useState<number>(DEFAULT_HYPER_DUSK_DURATION);
  const [hdProjectId, setHdProjectId] = useState<string | null>(null);
  const [hdImageId, setHdImageId] = useState<string | null>(null);
  const [hdProject, setHdProject] = useState<HyperDuskProjectLite | null>(null);
  const [hdImages, setHdImages] = useState<HyperDuskProjectImageLite[]>([]);
  const [hdSelectedVariantId, setHdSelectedVariantId] = useState<string | null>(null);
  const [hdGenerating, setHdGenerating] = useState(false);
  const [hdApproving, setHdApproving] = useState(false);
  const [hdAnimating, setHdAnimating] = useState(false);
  const [hdSavingAssets, setHdSavingAssets] = useState(false);
  const hdAssetsCopiedRef = useRef(false);

  // Analytics cache per demo
  const [analyticsCache, setAnalyticsCache] = useState<Record<string, { views: number; downloads: number }>>({});

  const loadDemos = useCallback(async () => {
    setLoading(true);
    const data = await getShareDemos(statusFilter, searchTerm);
    setDemos(data);

    // Load light analytics for list view
    const cache: Record<string, { views: number; downloads: number }> = {};
    await Promise.all(
      data.map(async (d) => {
        const a = await getShareDemoAnalytics(d.id);
        cache[d.id] = { views: a.views, downloads: a.download_completes };
      })
    );
    setAnalyticsCache(cache);
    setLoading(false);
  }, [statusFilter, searchTerm]);

  useEffect(() => { loadDemos(); }, [loadDemos]);

  const resetForm = () => {
    setEditingDemo(null);
    setStep(1);
    setInternalName('');
    setWorkflowType('photoclear');
    setOriginalFile(null);
    setProcessedFile(null);
    setOriginalPreview(null);
    setProcessedPreview(null);
    setOriginalUrl(null);
    setProcessedUrl(null);
    setProcessedVideoUrl(null);
    setVideoFile(null);
    setVideoPreview(null);
    setUploadingVideo(false);
    setSlug('');
    setHeadline("Here's how one of your listings could look with Photoclear");
    setSubheadline('A quick before-and-after example showing how clutter can be removed and presentation improved in seconds.');
    setTourUrl('');
    setAgencyName('');
    setAgencyLogoUrl(null);
    setSourceLabel('');
    setCtaMode('signup');
    setCtaSignupUrl('https://photoclear.rehub.studio/signup');
    setCtaDemoUrl('https://calendly.com/rehub');
    setDownloadMode('free');
    setWatermarkEnabled(false);
    setBrandingEnabled(true);
    setAdminNotes('');
    setListingUrl('');
    setAppsUsed([]);
    setStatus('draft');
    setProcessError(null);
    setHdSceneType('exterior');
    setHdPresetId(DEFAULT_HYPER_DUSK_PRESET_ID);
    setHdAnimationStyle(DEFAULT_HYPER_DUSK_ANIMATION_STYLE);
    setHdDuration(DEFAULT_HYPER_DUSK_DURATION);
    setHdProjectId(null);
    setHdImageId(null);
    setHdProject(null);
    setHdImages([]);
    setHdSelectedVariantId(null);
    setHdGenerating(false);
    setHdApproving(false);
    setHdAnimating(false);
    setHdSavingAssets(false);
    hdAssetsCopiedRef.current = false;
  };

  const openCreate = () => { resetForm(); setView('create'); };

  const openEdit = (demo: ShareDemo) => {
    setEditingDemo(demo);
    setInternalName(demo.internal_name);
    setWorkflowType(demo.workflow_type || 'photoclear');
    setOriginalUrl(demo.original_image_url);
    setProcessedUrl(demo.processed_image_url);
    setProcessedVideoUrl(demo.processed_video_url || null);
    setVideoFile(null);
    setVideoPreview(demo.processed_video_url || null);
    setOriginalPreview(demo.original_image_url);
    setProcessedPreview(demo.processed_image_url);
    setSlug(demo.slug);
    setHeadline(demo.public_headline);
    setSubheadline(demo.public_subheadline);
    setTourUrl(demo.tour_url || '');
    setAgencyName(demo.agency_name || '');
    setAgencyLogoUrl(demo.agency_logo_url || null);
    setSourceLabel(demo.source_label || '');
    setCtaMode(demo.cta_mode);
    setCtaSignupUrl(demo.cta_signup_url);
    setCtaDemoUrl(demo.cta_demo_url);
    setDownloadMode(demo.download_mode);
    setWatermarkEnabled(demo.watermark_enabled);
    setBrandingEnabled(demo.branding_enabled);
    setAdminNotes(demo.admin_notes || '');
    setListingUrl(demo.listing_url || '');
    setAppsUsed(Array.isArray(demo.apps_used) ? demo.apps_used : []);
    setStatus(demo.status);
    setHdSceneType((demo.hyperdusk_scene_type as ShareDemoSceneType) || 'exterior');
    setHdPresetId(demo.hyperdusk_preset_id || DEFAULT_HYPER_DUSK_PRESET_ID);
    setHdAnimationStyle((demo.hyperdusk_animation_style as HyperDuskAnimationStyle) || DEFAULT_HYPER_DUSK_ANIMATION_STYLE);
    setHdDuration(demo.hyperdusk_duration_seconds || DEFAULT_HYPER_DUSK_DURATION);
    setHdProjectId(demo.hyperdusk_project_id || null);
    setHdImageId(demo.hyperdusk_image_id || null);
    setHdProject(null);
    setHdImages([]);
    setHdSelectedVariantId(null);
    hdAssetsCopiedRef.current = !!(demo.processed_image_url && (demo.workflow_type !== 'hyperdusk' || demo.processed_video_url));
    const needsVideo = demo.workflow_type === 'hyperdusk' || demo.workflow_type === 'video';
    const completed = demo.workflow_type === 'tour'
      ? !!demo.tour_url && !!demo.original_image_url
      : needsVideo
      ? !!demo.processed_video_url
      : demo.workflow_type === 'staging'
      ? !!demo.original_image_url && !!demo.processed_image_url
      : !!demo.processed_image_url;
    setStep(completed ? 3 : demo.original_image_url ? 2 : 1);
    setView('edit');
  };

  const handleBack = () => { resetForm(); setView('list'); };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOriginalFile(file);
    setOriginalPreview(URL.createObjectURL(file));
    if (workflowType !== 'staging') {
      setProcessedPreview(null);
      setProcessedUrl(null);
    }
    setProcessError(null);
  };

  const handleAfterFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProcessedFile(file);
    setProcessedPreview(URL.createObjectURL(file));
    setProcessedUrl(null);
    setProcessError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    setOriginalFile(file);
    setOriginalPreview(URL.createObjectURL(file));
    if (workflowType !== 'staging') {
      setProcessedPreview(null);
      setProcessedUrl(null);
    }
    setProcessError(null);
  };

  const handleAfterDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    setProcessedFile(file);
    setProcessedPreview(URL.createObjectURL(file));
    setProcessedUrl(null);
    setProcessError(null);
  };

  const handleUploadAndContinue = async () => {
    if (!originalFile && !originalUrl) return;
    setSaving(true);

    try {
      if (originalFile) {
        const demoId = editingDemo?.id || crypto.randomUUID();
        const url = await uploadShareDemoImage(originalFile, demoId, 'original');
        if (!url) throw new Error('Upload failed');
        setOriginalUrl(url);

        if (!editingDemo) {
          const generatedSlug = await generateUniqueSlug(internalName || 'demo');
          setSlug(generatedSlug);
          const result = await createShareDemo({
            id: demoId,
            internal_name: internalName || 'Untitled Demo',
            original_image_url: url,
            slug: generatedSlug,
            created_by: userId,
            workflow_type: workflowType,
          });
          if (result.demo) setEditingDemo(result.demo);
        } else {
          await updateShareDemo(editingDemo.id, {
            original_image_url: url,
            workflow_type: workflowType,
          });
        }
      }
      setStep(2);
    } catch (err: any) {
      alert('Upload failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleProcess = async () => {
    const fileToProcess = originalFile;
    const urlToProcess = originalUrl;
    if (!fileToProcess && !urlToProcess) return;

    setProcessing(true);
    setProcessError(null);

    try {
      let file = fileToProcess;
      if (!file && urlToProcess) {
        const response = await fetch(urlToProcess);
        const blob = await response.blob();
        file = new File([blob], 'original.jpg', { type: blob.type });
      }
      if (!file) throw new Error('No file available');

      const operation = DEMO_OPERATION[photoMode];
      const prices = await fetchPhotoclearPrices();
      const quotedCost = creditsForOperation(operation, prices);
      if (quotedCost == null) throw new Error('This edit is not available.');
      const reserved = await reservePhotoclearAction({
        userId,
        operation,
        quotedCost,
      });
      if (reserved.ok === false) {
        if (reserved.error === 'INSUFFICIENT_CREDITS') {
          throw new Error('Not enough credits for this edit.');
        }
        if (reserved.error === 'STALE_QUOTE') {
          throw new Error('The price changed. Try again.');
        }
        throw new Error(reserved.message || 'Could not charge credits.');
      }

      let cropped: string;
      try {
        const prepared = await prepareForGemini(file);
        const prompt = PHOTO_MODES.find((m) => m.id === photoMode)?.prompt ?? AUTO_DECLUTTER_PROMPT;
        const resultBase64 = await declutterImage(prepared.imageData, prompt, prepared.aspectRatio);
        cropped = await cropResult(resultBase64, prepared.restoreRect);
        await finalizePhotoclearAction(userId, reserved.refId);
      } catch (genErr) {
        await releasePhotoclearAction(userId, reserved.refId);
        throw genErr;
      }

      setProcessedPreview(cropped);

      if (editingDemo) {
        const procUrl = await uploadShareDemoImage(cropped, editingDemo.id, 'processed');
        if (procUrl) {
          setProcessedUrl(procUrl);
          await updateShareDemo(editingDemo.id, { processed_image_url: procUrl });
        }
      }

      setStep(3);
    } catch (err: any) {
      console.error('Processing failed:', err);
      setProcessError(err.message || 'Processing failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // ─── 360 Tour: paste a tour URL + a cover/preview image ──────────
  const handleTourContinue = async () => {
    const url = tourUrl.trim();
    if (!url) { setProcessError('Add the 360 tour URL.'); return; }
    if (!/^https?:\/\//i.test(url)) { setProcessError('Tour URL must start with http(s)://'); return; }

    setSaving(true);
    setProcessError(null);
    try {
      const demoId = editingDemo?.id || crypto.randomUUID();

      let coverUrl = originalUrl;
      if (originalFile) {
        const uploaded = await uploadShareDemoImage(originalFile, demoId, 'original');
        if (!uploaded) throw new Error('Could not save the cover image.');
        coverUrl = uploaded;
        setOriginalUrl(uploaded);
        setOriginalPreview(uploaded);
        // Use the cover as the "processed" image too so previews / publish
        // checks (which expect processed_image_url) work for tours.
        setProcessedUrl(uploaded);
        setProcessedPreview(uploaded);
      }
      // Cover image is optional for tours — fall back to the branded default so
      // the share page (which needs a processed_image_url) still renders and the
      // publish gate (canPublish) is satisfied.
      if (!coverUrl) {
        coverUrl = DEFAULT_TOUR_COVER_URL;
        setOriginalUrl(coverUrl);
        setProcessedUrl(coverUrl);
      }

      if (!editingDemo) {
        const generatedSlug = await generateUniqueSlug(internalName || 'tour');
        setSlug(generatedSlug);
        const result = await createShareDemo({
          id: demoId,
          internal_name: internalName || 'Untitled Tour',
          original_image_url: coverUrl,
          processed_image_url: coverUrl,
          slug: generatedSlug,
          created_by: userId,
          workflow_type: 'tour',
          tour_url: url,
        });
        if (!result.demo) throw new Error(result.error || 'Could not create demo.');
        setEditingDemo(result.demo);
      } else {
        await updateShareDemo(editingDemo.id, {
          workflow_type: 'tour',
          tour_url: url,
          original_image_url: coverUrl,
          processed_image_url: coverUrl,
        });
      }

      setOriginalFile(null);
      setStep(2);
    } catch (err: any) {
      console.error('Tour setup failed:', err);
      setProcessError(err.message || 'Could not save the tour. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Manual video upload ──────────────────────────────────────
  const handleVideoSelect = (file: File | undefined) => {
    if (!file || !file.type.startsWith('video/')) return;
    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
    setProcessError(null);
  };

  // Video-first flow: upload the video and auto-derive a poster from its
  // first frame, creating the demo on the fly if needed.
  const handleVideoContinue = async () => {
    if (!videoFile && !processedVideoUrl) return;
    setUploadingVideo(true);
    setProcessError(null);
    try {
      let demo = editingDemo;
      const demoId = demo?.id || crypto.randomUUID();

      // Build a poster from the chosen video (required: original_image_url is NOT NULL).
      let posterUrl = originalUrl;
      if (videoFile) {
        const posterData = await captureVideoPoster(videoFile);
        const uploaded = await uploadShareDemoImage(posterData, demoId, 'original');
        if (!uploaded) throw new Error('Could not save the poster image.');
        posterUrl = uploaded;
        setOriginalUrl(uploaded);
        setOriginalPreview(uploaded);
        setProcessedUrl(uploaded);
        setProcessedPreview(uploaded);
      }
      if (!posterUrl) throw new Error('A poster image is required.');

      if (!demo) {
        const generatedSlug = await generateUniqueSlug(internalName || 'demo');
        setSlug(generatedSlug);
        const result = await createShareDemo({
          id: demoId,
          internal_name: internalName || 'Untitled Demo',
          original_image_url: posterUrl,
          processed_image_url: posterUrl,
          slug: generatedSlug,
          created_by: userId,
          workflow_type: 'video',
        });
        if (!result.demo) throw new Error(result.error || 'Could not create demo.');
        demo = result.demo;
        setEditingDemo(result.demo);
      }

      if (videoFile) {
        const vurl = await uploadShareDemoVideo(videoFile, demoId);
        if (!vurl) throw new Error('Video upload failed');
        setProcessedVideoUrl(vurl);
        setVideoFile(null);
        await updateShareDemo(demoId, {
          workflow_type: 'video',
          processed_video_url: vurl,
          original_image_url: posterUrl,
          processed_image_url: posterUrl,
        });
      }

      setStep(2);
    } catch (err: any) {
      console.error('Video upload failed:', err);
      setProcessError(err.message || 'Video upload failed. Please try again.');
    } finally {
      setUploadingVideo(false);
    }
  };

  // ─── Staging: upload before + after images (no AI) ────────────
  const handleStagingContinue = async () => {
    const hasBefore = originalFile || originalUrl;
    const hasAfter = processedFile || processedUrl;
    if (!hasBefore || !hasAfter) return;

    setSaving(true);
    setProcessError(null);
    try {
      const demoId = editingDemo?.id || crypto.randomUUID();

      let beforeUrl = originalUrl;
      if (originalFile) {
        const uploaded = await uploadShareDemoImage(originalFile, demoId, 'original');
        if (!uploaded) throw new Error('Before image upload failed.');
        beforeUrl = uploaded;
        setOriginalUrl(uploaded);
        setOriginalPreview(uploaded);
      }

      let afterUrl = processedUrl;
      if (processedFile) {
        const uploaded = await uploadShareDemoImage(processedFile, demoId, 'processed');
        if (!uploaded) throw new Error('After image upload failed.');
        afterUrl = uploaded;
        setProcessedUrl(uploaded);
        setProcessedPreview(uploaded);
      }

      if (!beforeUrl || !afterUrl) throw new Error('Both before and after images are required.');

      if (!editingDemo) {
        const generatedSlug = await generateUniqueSlug(internalName || 'staging');
        setSlug(generatedSlug);
        const result = await createShareDemo({
          id: demoId,
          internal_name: internalName || 'Untitled Staging Demo',
          original_image_url: beforeUrl,
          processed_image_url: afterUrl,
          slug: generatedSlug,
          created_by: userId,
          workflow_type: 'staging',
        });
        if (!result.demo) throw new Error(result.error || 'Could not create demo.');
        setEditingDemo(result.demo);
      } else {
        await updateShareDemo(editingDemo.id, {
          workflow_type: 'staging',
          original_image_url: beforeUrl,
          processed_image_url: afterUrl,
        });
      }

      setOriginalFile(null);
      setProcessedFile(null);
      setStep(2);
    } catch (err: any) {
      console.error('Staging upload failed:', err);
      setProcessError(err.message || 'Upload failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Optional poster override (step 2 of the video workflow).
  const handlePosterReplace = async () => {
    if (!editingDemo || !originalFile) return;
    setSaving(true);
    setProcessError(null);
    try {
      const url = await uploadShareDemoImage(originalFile, editingDemo.id, 'original');
      if (!url) throw new Error('Poster upload failed');
      setOriginalUrl(url);
      setOriginalPreview(url);
      setProcessedUrl(url);
      setProcessedPreview(url);
      setOriginalFile(null);
      await updateShareDemo(editingDemo.id, {
        original_image_url: url,
        processed_image_url: url,
      });
    } catch (err: any) {
      setProcessError(err.message || 'Poster upload failed.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Hyper Dusk pipeline ──────────────────────────────────────
  const selectedHdPreset = HYPER_DUSK_PRESETS.find((p) => p.id === hdPresetId) ?? HYPER_DUSK_PRESETS[0];
  const hdActiveImage = hdImages[0] ?? null;
  const hdVariants = hdActiveImage?.dusk_variants ?? [];
  const hdSelectedVariant =
    hdVariants.find((v) => v.id === (hdSelectedVariantId ?? hdActiveImage?.selected_dusk_variant_id)) ??
    hdVariants[hdVariants.length - 1] ?? null;
  const hdDuskPreview = hdSelectedVariant?.url || hdActiveImage?.dusk_image_url || null;
  const hdGenStatus = hdActiveImage?.generation_status;
  const hdIsDuskReady = !!hdDuskPreview && (
    hdGenStatus === 'awaiting_user_approval' ||
    hdGenStatus === 'dusk_ready' ||
    hdGenStatus === 'kling_processing' ||
    hdGenStatus === 'animation_ready' ||
    hdGenStatus === 'completed'
  );
  const hdIsApproved =
    hdGenStatus === 'dusk_ready' ||
    hdGenStatus === 'kling_processing' ||
    hdGenStatus === 'animation_ready' ||
    hdGenStatus === 'completed';
  const hdIsAnimationRunning =
    hdProject?.status === 'rendering' ||
    hdProject?.status === 'generating_clips' ||
    hdGenStatus === 'kling_processing';
  const hdIsCompleted = hdProject?.status === 'completed' && (hdActiveImage?.clip_url || hdProject?.final_video_url);

  const refreshHyperDuskProject = useCallback(async (projectId: string) => {
    const data = await fetchHyperDuskShareProject(projectId);
    setHdProject(data.project);
    setHdImages(data.images);
    if (data.images[0]?.id) setHdImageId(data.images[0].id);
    return data;
  }, []);

  // Auto-load existing hyperdusk project when editing
  useEffect(() => {
    if (workflowType !== 'hyperdusk') return;
    if (!hdProjectId) return;
    if (hdProject) return;
    void refreshHyperDuskProject(hdProjectId);
  }, [workflowType, hdProjectId, hdProject, refreshHyperDuskProject]);

  // Poll while generation/animation is running
  useEffect(() => {
    if (workflowType !== 'hyperdusk' || !hdProjectId) return;
    const status = hdProject?.status;
    const needsPoll =
      status === 'queued' || status === 'generating_clips' ||
      status === 'editing' || status === 'rendering' ||
      hdGenStatus === 'dusk_processing' || hdGenStatus === 'kling_processing';
    if (!needsPoll) return;

    const timer = setInterval(async () => {
      try {
        if (hdGenStatus === 'kling_processing' || status === 'rendering') {
          await pollHyperDuskShareStatus(hdProjectId);
        }
        await refreshHyperDuskProject(hdProjectId);
      } catch (err) {
        console.warn('Hyper Dusk polling failed:', err);
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [workflowType, hdProjectId, hdProject?.status, hdGenStatus, refreshHyperDuskProject]);

  // When animation completes, copy assets into the public images bucket
  // and persist them to the share demo so the public page can serve them.
  useEffect(() => {
    if (workflowType !== 'hyperdusk') return;
    if (!editingDemo || !hdIsCompleted) return;
    if (hdAssetsCopiedRef.current) return;
    hdAssetsCopiedRef.current = true;

    const stillUrl = hdDuskPreview;
    const videoUrl = hdActiveImage?.clip_url || hdProject?.final_video_url || null;
    if (!stillUrl || !videoUrl) {
      hdAssetsCopiedRef.current = false;
      return;
    }

    (async () => {
      setHdSavingAssets(true);
      try {
        const [imgPublic, vidPublic] = await Promise.all([
          copyHyperDuskAssetToShareDemo(stillUrl, editingDemo.id, 'processed_image'),
          copyHyperDuskAssetToShareDemo(videoUrl, editingDemo.id, 'processed_video'),
        ]);
        if (!imgPublic || !vidPublic) throw new Error('Failed to copy assets to share-demo bucket');

        setProcessedUrl(imgPublic);
        setProcessedPreview(imgPublic);
        setProcessedVideoUrl(vidPublic);

        const updates: Partial<ShareDemo> = {
          processed_image_url: imgPublic,
          processed_video_url: vidPublic,
          workflow_type: 'hyperdusk',
          hyperdusk_project_id: hdProjectId,
          hyperdusk_image_id: hdImageId,
          hyperdusk_preset_id: hdPresetId,
          hyperdusk_scene_type: hdSceneType,
          hyperdusk_animation_style: hdAnimationStyle,
          hyperdusk_duration_seconds: hdDuration,
        };
        await updateShareDemo(editingDemo.id, updates);
      } catch (err: any) {
        console.error('Failed to persist Hyper Dusk assets:', err);
        setProcessError(err.message || 'Failed to save Hyper Dusk assets');
        hdAssetsCopiedRef.current = false;
      } finally {
        setHdSavingAssets(false);
      }
    })();
  }, [
    workflowType, editingDemo, hdIsCompleted, hdDuskPreview,
    hdActiveImage?.clip_url, hdProject?.final_video_url,
    hdProjectId, hdImageId, hdPresetId, hdSceneType, hdAnimationStyle, hdDuration,
  ]);

  const handleHyperDuskGenerate = async () => {
    if (!editingDemo || !originalUrl) return;
    setProcessError(null);
    setHdGenerating(true);
    try {
      let projectId = hdProjectId;
      let imageId = hdImageId;

      if (!projectId) {
        // Hyper Dusk's Kling step needs the original to live in the
        // imagemotion-uploads bucket (so it can build short-lived signed URLs
        // by storage path). Pull the share-demo original and re-upload it.
        let hdOriginal: { url: string; path: string } | null = null;
        if (originalFile) {
          hdOriginal = await uploadHyperDuskImage(originalFile, userId);
        } else {
          const resp = await fetch(originalUrl);
          if (!resp.ok) throw new Error(`Could not fetch original image (${resp.status})`);
          const blob = await resp.blob();
          const ext = blob.type.includes('png') ? 'png' : 'jpg';
          const file = new File([blob], `share-demo-original.${ext}`, { type: blob.type || 'image/jpeg' });
          hdOriginal = await uploadHyperDuskImage(file, userId);
        }
        if (!hdOriginal) throw new Error('Failed to stage original image for Hyper Dusk');

        const created = await createHyperDuskShareProject({
          userId,
          name: internalName || 'Share Demo Hyper Dusk',
          image: { url: hdOriginal.url, path: hdOriginal.path },
          aspectRatio: '16:9',
          animationStyle: hdAnimationStyle,
          durationSeconds: hdDuration,
        });
        if (!created) throw new Error('Failed to create Hyper Dusk project');
        projectId = created.projectId;
        imageId = created.imageId;
        setHdProjectId(projectId);
        setHdImageId(imageId);

        await updateShareDemo(editingDemo.id, {
          workflow_type: 'hyperdusk',
          hyperdusk_project_id: projectId,
          hyperdusk_image_id: imageId,
          hyperdusk_preset_id: hdPresetId,
          hyperdusk_scene_type: hdSceneType,
          hyperdusk_animation_style: hdAnimationStyle,
          hyperdusk_duration_seconds: hdDuration,
        });
      }

      hdAssetsCopiedRef.current = false;
      const result = await generateHyperDuskSharePreview(projectId!, {
        presetId: selectedHdPreset.id,
        nanoPrompt: hdSceneType === 'interior' ? selectedHdPreset.nanoPromptInterior : selectedHdPreset.nanoPrompt,
        klingPromptBase: hdSceneType === 'interior' ? selectedHdPreset.klingPromptBaseInterior : selectedHdPreset.klingPromptBase,
        animationStyle: hdAnimationStyle,
        sceneType: hdSceneType,
      });
      if (!result.success) throw new Error(result.error || 'Dusk preview failed');

      await refreshHyperDuskProject(projectId!);
    } catch (err: any) {
      console.error('Hyper Dusk generate failed:', err);
      setProcessError(err.message || 'Hyper Dusk generation failed');
    } finally {
      setHdGenerating(false);
    }
  };

  const handleHyperDuskApprove = async () => {
    if (!hdProjectId || !hdImageId) return;
    setProcessError(null);
    setHdApproving(true);
    try {
      const result = await approveHyperDuskShareVariant(hdProjectId, hdImageId, hdSelectedVariant?.id);
      if (!result.success) throw new Error(result.error || 'Approve failed');
      await refreshHyperDuskProject(hdProjectId);
    } catch (err: any) {
      console.error('Hyper Dusk approve failed:', err);
      setProcessError(err.message || 'Approve failed');
    } finally {
      setHdApproving(false);
    }
  };

  const handleHyperDuskAnimate = async () => {
    if (!hdProjectId) return;
    setProcessError(null);
    setHdAnimating(true);
    hdAssetsCopiedRef.current = false;
    try {
      // Recovery: older share-demo projects were created without a
      // source_image_path. The Kling step needs one to mint a signed URL,
      // so stage the original into imagemotion-uploads and patch the row.
      if (hdActiveImage && !hdActiveImage.source_image_path && originalUrl) {
        const resp = await fetch(originalUrl);
        if (!resp.ok) throw new Error(`Could not fetch original image (${resp.status})`);
        const blob = await resp.blob();
        const ext = blob.type.includes('png') ? 'png' : 'jpg';
        const file = new File([blob], `share-demo-original.${ext}`, { type: blob.type || 'image/jpeg' });
        const staged = await uploadHyperDuskImage(file, userId);
        if (!staged) throw new Error('Failed to stage original image for Hyper Dusk');

        const { error: patchErr } = await supabase
          .from('imagemotion_project_images')
          .update({
            source_image_url: staged.url,
            source_image_path: staged.path,
            image_url: staged.url,
            image_path: staged.path,
            generation_status: hdActiveImage.generation_status === 'failed' ? 'dusk_ready' : hdActiveImage.generation_status,
            generation_error: null,
          })
          .eq('id', hdActiveImage.id);
        if (patchErr) throw new Error(`Failed to patch image row: ${patchErr.message}`);

        await refreshHyperDuskProject(hdProjectId);
      }

      const result = await generateHyperDuskShareAnimation(hdProjectId, {
        durationSeconds: hdDuration,
        animationStyle: hdAnimationStyle,
        selectedVariantId: hdSelectedVariant?.id,
      });
      if (!result.success) throw new Error(result.error || 'Animation failed');
      await refreshHyperDuskProject(hdProjectId);
    } catch (err: any) {
      console.error('Hyper Dusk animate failed:', err);
      setProcessError(err.message || 'Animation failed');
    } finally {
      setHdAnimating(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!editingDemo) return;
    setSaving(true);

    try {
      const updates: Partial<ShareDemo> = {
        internal_name: internalName,
        slug,
        public_headline: headline,
        public_subheadline: subheadline,
        agency_name: agencyName || null,
        agency_logo_url: agencyLogoUrl || null,
        tour_url: workflowType === 'tour' ? (tourUrl.trim() || null) : undefined,
        source_label: sourceLabel || null,
        cta_mode: ctaMode,
        cta_signup_url: ctaSignupUrl,
        cta_demo_url: ctaDemoUrl,
        download_mode: downloadMode,
        watermark_enabled: watermarkEnabled,
        branding_enabled: brandingEnabled,
        admin_notes: adminNotes || null,
        apps_used: appsUsed,
        listing_url: listingUrl.trim() || null,
        status,
      };

      if (status === 'published' && editingDemo.status !== 'published') {
        (updates as any).published_at = new Date().toISOString();
      }

      const result = await updateShareDemo(editingDemo.id, updates);
      if (result.demo) setEditingDemo(result.demo);
      setStep(4);
    } catch (err: any) {
      alert('Save failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handlePublishToggle = async (demo: ShareDemo) => {
    const newStatus: ShareDemoStatus = demo.status === 'published' ? 'unpublished' : 'published';
    if (newStatus === 'published') {
      const needsVideo = demo.workflow_type === 'hyperdusk' || demo.workflow_type === 'video';
      const ready = needsVideo
        ? !!demo.processed_video_url
        : !!demo.processed_image_url;
      if (!ready) {
        alert(
          demo.workflow_type === 'hyperdusk'
            ? 'Cannot publish without a generated Hyper Dusk animation.'
            : demo.workflow_type === 'video'
            ? 'Cannot publish without an uploaded video.'
            : demo.workflow_type === 'staging'
            ? 'Cannot publish without both before and after images.'
            : 'Cannot publish without a processed image.'
        );
        return;
      }
    }
    const updates: Partial<ShareDemo> = { status: newStatus };
    if (newStatus === 'published') (updates as any).published_at = new Date().toISOString();
    await updateShareDemo(demo.id, updates);
    loadDemos();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this demo? This cannot be undone.')) return;
    await deleteShareDemo(id);
    loadDemos();
  };

  const copyLink = (slug: string, workflow: ShareDemoWorkflowType) => {
    const url = getShareDemoUrl(workflow, slug);
    navigator.clipboard.writeText(url);
    setCopiedId(slug);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // ─── LIST VIEW ────────────────────────────────────────────────
  if (view === 'list') {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Controls */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex bg-slate-900 rounded-lg border border-slate-800 overflow-hidden">
              {(['all', 'draft', 'published', 'unpublished'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setStatusFilter(f)}
                  className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                    statusFilter === f ? 'bg-orange-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search demos..."
                className="pl-9 pr-3 py-1.5 bg-slate-900 border border-slate-800 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500 w-48"
              />
            </div>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus size={16} />
            New Demo
          </button>
        </div>

        {/* Demo list */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="text-slate-400 animate-spin" />
          </div>
        ) : demos.length === 0 ? (
          <div className="text-center py-16 space-y-3">
            <ImageIcon size={40} className="mx-auto text-slate-600" />
            <p className="text-slate-400">No share demos yet</p>
            <button onClick={openCreate} className="text-orange-400 hover:text-orange-300 text-sm font-medium">
              Create your first demo
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {demos.map((demo) => (
              <div
                key={demo.id}
                className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center gap-4 hover:border-slate-700 transition-colors"
              >
                {/* Thumbnail */}
                <div className="w-20 h-14 rounded-lg overflow-hidden bg-slate-800 flex-shrink-0">
                  {demo.processed_image_url ? (
                    <img
                      src={getThumbnailUrl(demo.processed_image_url, { width: 160, height: 112 }) || demo.processed_image_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : demo.original_image_url ? (
                    <img
                      src={getThumbnailUrl(demo.original_image_url, { width: 160, height: 112 }) || demo.original_image_url}
                      alt=""
                      className="w-full h-full object-cover opacity-50"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon size={20} className="text-slate-600" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-white truncate">{demo.internal_name}</h3>
                    <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${STATUS_COLORS[demo.status]}`}>
                      {demo.status}
                    </span>
                    {demo.workflow_type === 'hyperdusk' && (
                      <span className="px-2 py-0.5 text-[10px] font-medium rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-300 flex items-center gap-1">
                        <Moon size={9} /> Hyper Dusk
                      </span>
                    )}
                    {demo.workflow_type === 'video' && (
                      <span className="px-2 py-0.5 text-[10px] font-medium rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 flex items-center gap-1">
                        <Film size={9} /> Video
                      </span>
                    )}
                    {demo.workflow_type === 'tour' && (
                      <span className="px-2 py-0.5 text-[10px] font-medium rounded-full border border-sky-500/30 bg-sky-500/10 text-sky-300 flex items-center gap-1">
                        <Globe size={9} /> 360 Tour
                      </span>
                    )}
                    {demo.workflow_type === 'staging' && (
                      <span className="px-2 py-0.5 text-[10px] font-medium rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300 flex items-center gap-1">
                        <Sofa size={9} /> Staging
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span>{getShareDemoHost(demo.workflow_type)}/share/{demo.slug}</span>
                    {demo.agency_name && <span>{demo.agency_name}</span>}
                    <span>{new Date(demo.created_at).toLocaleDateString()}</span>
                    {analyticsCache[demo.id] && (
                      <>
                        <span className="flex items-center gap-1"><Eye size={10} /> {analyticsCache[demo.id].views}</span>
                        <span className="flex items-center gap-1"><Sparkles size={10} /> {analyticsCache[demo.id].downloads}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {demo.status === 'published' && (
                    <a
                      href={getShareDemoUrl(demo.workflow_type, demo.slug)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                      title="Preview"
                    >
                      <ExternalLink size={16} />
                    </a>
                  )}
                  <button
                    onClick={() => copyLink(demo.slug, demo.workflow_type)}
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                    title="Copy link"
                  >
                    {copiedId === demo.slug ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                  </button>
                  <button
                    onClick={() => openEdit(demo)}
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <ChevronRight size={16} />
                  </button>
                  <button
                    onClick={() => handlePublishToggle(demo)}
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                    title={demo.status === 'published' ? 'Unpublish' : 'Publish'}
                  >
                    {demo.status === 'published' ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  <button
                    onClick={() => handleDelete(demo.id)}
                    className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─── CREATE / EDIT VIEW ───────────────────────────────────────
  const isEditing = view === 'edit';
  const canPublish =
    workflowType === 'hyperdusk'
      ? !!processedVideoUrl && !!processedUrl && !!originalUrl
      : workflowType === 'video'
      ? !!processedVideoUrl && !!originalUrl
      : workflowType === 'tour'
      ? !!tourUrl.trim()
      : !!processedUrl && !!originalUrl;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Back + title */}
      <div className="flex items-center gap-3">
        <button onClick={handleBack} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
          <ArrowLeft size={20} />
        </button>
        <h2 className="text-lg font-bold text-white">{isEditing ? 'Edit Demo' : 'New Demo'}</h2>
      </div>

      {/* Step indicators */}
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4].map((s) => (
          <button
            key={s}
            onClick={() => {
              if (s === 1) setStep(1);
              else if (s === 2 && originalUrl) setStep(2);
              else if (s === 3 && processedUrl) setStep(3);
              else if (s === 4 && processedUrl) setStep(4);
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              step === s ? 'bg-orange-600 text-white' : step > s ? 'bg-slate-800 text-orange-400' : 'bg-slate-900 text-slate-500'
            }`}
          >
            {s}. {s === 1
              ? (workflowType === 'video' ? 'Video' : workflowType === 'tour' ? 'Tour' : workflowType === 'staging' ? 'Images' : 'Image')
              : s === 2
              ? (workflowType === 'video' ? 'Poster' : workflowType === 'tour' || workflowType === 'staging' ? 'Preview' : 'Process')
              : s === 3 ? 'Configure' : 'Publish'}
          </button>
        ))}
      </div>

      {/* ── Step 1: Upload ─────────────────────────────────── */}
      {step === 1 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Internal Name</label>
            <input
              type="text"
              value={internalName}
              onChange={(e) => setInternalName(e.target.value)}
              placeholder="e.g. 123 High Street - Foxtons"
              className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Workflow</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {([
                {
                  id: 'photoclear' as const,
                  icon: <Sparkles size={18} className="text-orange-400" />,
                  title: 'Photoclear',
                  desc: 'AI declutter — clean before/after still image.',
                  ring: 'border-orange-500 bg-orange-900/20',
                },
                {
                  id: 'staging' as const,
                  icon: <Sofa size={18} className="text-violet-400" />,
                  title: 'Staging Project',
                  desc: 'Upload before and after images for a staging slider.',
                  ring: 'border-violet-500 bg-violet-900/20',
                },
                {
                  id: 'hyperdusk' as const,
                  icon: <Moon size={18} className="text-indigo-400" />,
                  title: 'Hyper Dusk',
                  desc: 'Day-to-night still + cinematic time-lapse video.',
                  ring: 'border-indigo-500 bg-indigo-900/20',
                },
                {
                  id: 'video' as const,
                  icon: <Film size={18} className="text-emerald-400" />,
                  title: 'Upload Video',
                  desc: 'Upload a video you made yourself + a poster image.',
                  ring: 'border-emerald-500 bg-emerald-900/20',
                },
                {
                  id: 'tour' as const,
                  icon: <Globe size={18} className="text-sky-400" />,
                  title: '360 Tour',
                  desc: 'Paste a virtual-tour URL — branded, embeddable share page.',
                  ring: 'border-sky-500 bg-sky-900/20',
                },
              ]).map((opt) => {
                const selected = workflowType === opt.id;
                const lockedOnEdit = isEditing && !!editingDemo?.processed_image_url && editingDemo.workflow_type !== opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      if (lockedOnEdit) return;
                      setWorkflowType(opt.id);
                      if (!isEditing && opt.id === 'staging') {
                        setHeadline("Here's how virtual staging could transform one of your listings");
                        setSubheadline('A before-and-after showing how furniture and styling can bring an empty room to life.');
                        setCtaSignupUrl('https://staging.rehub.studio/signup');
                        setAppsUsed((prev) => (prev.includes('staging') ? prev : ['staging', ...prev]));
                      }
                    }}
                    disabled={lockedOnEdit}
                    className={`p-3 rounded-xl border text-left transition-colors ${
                      selected
                        ? opt.ring
                        : 'border-slate-700 bg-slate-900 hover:border-slate-600'
                    } ${lockedOnEdit ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {opt.icon}
                      <span className="text-sm font-semibold text-white">{opt.title}</span>
                    </div>
                    <p className="text-xs text-slate-400">{opt.desc}</p>
                  </button>
                );
              })}
            </div>
            {isEditing && editingDemo?.processed_image_url && (
              <p className="text-[11px] text-slate-500 mt-2">
                Workflow can't be changed after the first processed result has been generated. Create a new demo to switch workflows.
              </p>
            )}
          </div>

          {workflowType === 'tour' && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">360 Tour URL</label>
              <input
                type="url"
                value={tourUrl}
                onChange={(e) => setTourUrl(e.target.value)}
                placeholder="https://360tours.rehub.studio/…"
                className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              />
              <p className="text-[11px] text-slate-500 mt-1">Paste the full virtual-tour link — it's embedded on the branded share page. A cover image is optional — add one below for the preview/thumbnail, or we'll use the 360 Tours branding.</p>
            </div>
          )}

          {workflowType === 'video' ? (
            !videoPreview ? (
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleVideoSelect(e.dataTransfer.files[0]); }}
                className="border-2 border-dashed border-slate-700 rounded-2xl p-12 text-center hover:border-emerald-500/50 transition-colors cursor-pointer"
                onClick={() => document.getElementById('share-demo-video-step1')?.click()}
              >
                <Film size={32} className="mx-auto mb-3 text-slate-500" />
                <p className="text-sm text-slate-400 mb-1">Drop a video here or click to upload</p>
                <p className="text-xs text-slate-600">MP4, WebM, or MOV — a poster image is generated automatically</p>
                <input
                  id="share-demo-video-step1"
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime"
                  onChange={(e) => handleVideoSelect(e.target.files?.[0])}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-xl overflow-hidden border border-slate-700 bg-slate-950">
                  <video
                    key={videoPreview}
                    src={videoPreview}
                    className="w-full max-h-80 bg-black"
                    controls
                    muted
                    loop
                    playsInline
                    poster={originalPreview || undefined}
                  />
                </div>
                {!processedVideoUrl && (
                  <button
                    onClick={() => { setVideoFile(null); setVideoPreview(null); }}
                    className="text-sm text-slate-400 hover:text-white"
                  >
                    Change video
                  </button>
                )}
              </div>
            )
          ) : workflowType === 'staging' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300">Before</label>
                {!originalPreview ? (
                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDrop}
                    className="border-2 border-dashed border-slate-700 rounded-2xl p-8 text-center hover:border-violet-500/50 transition-colors cursor-pointer min-h-[180px] flex flex-col items-center justify-center"
                    onClick={() => document.getElementById('share-demo-before')?.click()}
                  >
                    <Upload size={28} className="mb-2 text-slate-500" />
                    <p className="text-sm text-slate-400">Drop before image or click</p>
                    <p className="text-xs text-slate-600 mt-1">JPEG, PNG, or WebP</p>
                    <input
                      id="share-demo-before"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="rounded-xl overflow-hidden border border-slate-700 bg-slate-900">
                      <img src={originalPreview} alt="Before" className="w-full max-h-56 object-contain" />
                    </div>
                    <button
                      onClick={() => { setOriginalFile(null); setOriginalPreview(null); setOriginalUrl(null); }}
                      className="text-sm text-slate-400 hover:text-white"
                    >
                      Change before
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300">After</label>
                {!processedPreview ? (
                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleAfterDrop}
                    className="border-2 border-dashed border-slate-700 rounded-2xl p-8 text-center hover:border-violet-500/50 transition-colors cursor-pointer min-h-[180px] flex flex-col items-center justify-center"
                    onClick={() => document.getElementById('share-demo-after')?.click()}
                  >
                    <Upload size={28} className="mb-2 text-slate-500" />
                    <p className="text-sm text-slate-400">Drop after image or click</p>
                    <p className="text-xs text-slate-600 mt-1">JPEG, PNG, or WebP</p>
                    <input
                      id="share-demo-after"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleAfterFileSelect}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="rounded-xl overflow-hidden border border-slate-700 bg-slate-900">
                      <img src={processedPreview} alt="After" className="w-full max-h-56 object-contain" />
                    </div>
                    <button
                      onClick={() => { setProcessedFile(null); setProcessedPreview(null); setProcessedUrl(null); }}
                      className="text-sm text-slate-400 hover:text-white"
                    >
                      Change after
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : !originalPreview ? (
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              className="border-2 border-dashed border-slate-700 rounded-2xl p-12 text-center hover:border-orange-500/50 transition-colors cursor-pointer"
              onClick={() => document.getElementById('share-demo-file')?.click()}
            >
              <Upload size={32} className="mx-auto mb-3 text-slate-500" />
              <p className="text-sm text-slate-400 mb-1">
                {workflowType === 'tour' ? 'Drop a cover image here or click to upload (optional)' : 'Drop an image here or click to upload'}
              </p>
              <p className="text-xs text-slate-600">JPEG, PNG, or WebP</p>
              <input
                id="share-demo-file"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-xl overflow-hidden border border-slate-700 bg-slate-900">
                <img src={originalPreview} alt="Original" className="w-full max-h-80 object-contain" />
              </div>
              <button
                onClick={() => { setOriginalFile(null); setOriginalPreview(null); setOriginalUrl(null); }}
                className="text-sm text-slate-400 hover:text-white"
              >
                Change image
              </button>
            </div>
          )}

          {processError && (workflowType === 'video' || workflowType === 'tour' || workflowType === 'staging') && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl text-sm">
              {processError}
            </div>
          )}

          <button
            onClick={
              workflowType === 'video'
                ? handleVideoContinue
                : workflowType === 'tour'
                ? handleTourContinue
                : workflowType === 'staging'
                ? handleStagingContinue
                : handleUploadAndContinue
            }
            disabled={
              saving || uploadingVideo || !internalName.trim() ||
              (workflowType === 'video'
                ? (!videoFile && !processedVideoUrl)
                : workflowType === 'tour'
                ? !tourUrl.trim()
                : workflowType === 'staging'
                ? ((!originalFile && !originalUrl) || (!processedFile && !processedUrl))
                : (!originalFile && !originalUrl))
            }
            className="w-full py-3 bg-orange-600 hover:bg-orange-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
          >
            {(saving || uploadingVideo) ? <Loader2 size={18} className="animate-spin" /> : <ChevronRight size={18} />}
            {(saving || uploadingVideo) ? 'Uploading...' : 'Continue'}
          </button>
        </div>
      )}

      {/* ── Step 2: Process ────────────────────────────────── */}
      {step === 2 && workflowType === 'photoclear' && (
        <div className="space-y-4">
          {originalPreview && (
            <div className="rounded-xl overflow-hidden border border-slate-700 bg-slate-900">
              <img src={originalPreview} alt="Original" className="w-full max-h-64 object-contain" />
            </div>
          )}

          {processedPreview && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-slate-300">Preview</h3>
              <BeforeAfterSlider beforeUrl={originalPreview!} afterUrl={processedPreview} />
            </div>
          )}

          {processError && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl text-sm">
              {processError}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Enhancement</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {PHOTO_MODES.map((m) => {
                const selected = photoMode === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setPhotoMode(m.id)}
                    disabled={processing}
                    className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50 ${
                      selected
                        ? 'border-orange-500 bg-orange-900/20 text-white'
                        : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-600'
                    }`}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleProcess}
              disabled={processing}
              className="flex-1 py-3 bg-orange-600 hover:bg-orange-500 disabled:bg-slate-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
            >
              {processing ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Sparkles size={18} />
              )}
              {processing
                ? 'Processing...'
                : `${processedPreview ? 'Re-run' : 'Run'}: ${PHOTO_MODES.find((m) => m.id === photoMode)?.label} — ${formatPhotoclearCreditLabel(quotePhotoclearOperation(DEMO_OPERATION[photoMode])?.creditCost ?? 1)}`}
            </button>
            {processedUrl && (
              <button
                onClick={() => setStep(3)}
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-colors"
              >
                Continue
              </button>
            )}
          </div>

          {processing && (
            <p className="text-xs text-slate-500 text-center">
              AI is enhancing the image. This usually takes 10-20 seconds.
            </p>
          )}
        </div>
      )}

      {/* ── Step 2 (Staging): preview slider ── */}
      {step === 2 && workflowType === 'staging' && (
        <div className="space-y-4">
          {originalPreview && processedPreview && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-slate-300">Preview</h3>
              <BeforeAfterSlider beforeUrl={originalPreview} afterUrl={processedPreview} />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-slate-300">Before</h3>
              {originalPreview ? (
                <div className="rounded-xl overflow-hidden border border-slate-700 bg-slate-900">
                  <img src={originalPreview} alt="Before" className="w-full max-h-48 object-contain" />
                </div>
              ) : null}
              <button
                onClick={() => document.getElementById('share-demo-staging-before')?.click()}
                className="text-sm text-slate-400 hover:text-white"
              >
                Replace before image
              </button>
              <input
                id="share-demo-staging-before"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-slate-300">After</h3>
              {processedPreview ? (
                <div className="rounded-xl overflow-hidden border border-slate-700 bg-slate-900">
                  <img src={processedPreview} alt="After" className="w-full max-h-48 object-contain" />
                </div>
              ) : null}
              <button
                onClick={() => document.getElementById('share-demo-staging-after')?.click()}
                className="text-sm text-slate-400 hover:text-white"
              >
                Replace after image
              </button>
              <input
                id="share-demo-staging-after"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleAfterFileSelect}
                className="hidden"
              />
            </div>
          </div>

          {processError && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl text-sm">
              {processError}
            </div>
          )}

          <div className="flex items-center gap-3">
            {(originalFile || processedFile) ? (
              <button
                onClick={handleStagingContinue}
                disabled={saving}
                className="flex-1 py-3 bg-violet-600 hover:bg-violet-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
              >
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                {saving ? 'Saving...' : 'Save images'}
              </button>
            ) : (
              <button
                onClick={() => setStep(3)}
                className="flex-1 py-3 bg-orange-600 hover:bg-orange-500 text-white font-semibold rounded-xl transition-colors"
              >
                Continue
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Step 2 (360 Tour): preview the embedded tour ── */}
      {step === 2 && workflowType === 'tour' && (
        <div className="space-y-4">
          {tourUrl.trim() && (
            <div className="rounded-xl overflow-hidden border border-slate-700 bg-slate-950 aspect-video">
              <iframe
                key={tourUrl}
                src={tourUrl.trim()}
                title="360 tour preview"
                className="w-full h-full"
                allow="accelerometer; gyroscope; fullscreen; xr-spatial-tracking"
                allowFullScreen
              />
            </div>
          )}
          <p className="text-xs text-slate-500 text-center">
            If the tour doesn't appear, the provider may block embedding — viewers will still get an "Open tour" button on the share page.{' '}
            <a href={tourUrl.trim()} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:text-sky-300 underline">Open in new tab</a>
          </p>

          {originalPreview && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-slate-300">Cover image (preview / thumbnail)</h3>
              <div className="rounded-xl overflow-hidden border border-slate-700 bg-slate-900">
                <img src={originalPreview} alt="Cover" className="w-full max-h-64 object-contain" />
              </div>
              <button
                onClick={() => document.getElementById('share-demo-tour-cover')?.click()}
                className="text-sm text-slate-400 hover:text-white"
              >
                Replace cover image
              </button>
              <input
                id="share-demo-tour-cover"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {processError && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl text-sm">
              {processError}
            </div>
          )}

          <div className="flex items-center gap-3">
            {originalFile ? (
              <button
                onClick={handleTourContinue}
                disabled={saving}
                className="flex-1 py-3 bg-sky-600 hover:bg-sky-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
              >
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                {saving ? 'Saving...' : 'Save cover'}
              </button>
            ) : (
              <button
                onClick={() => setStep(3)}
                className="flex-1 py-3 bg-orange-600 hover:bg-orange-500 text-white font-semibold rounded-xl transition-colors"
              >
                Continue
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Step 2 (Upload Video): poster / thumbnail (auto-generated, optional override) ── */}
      {step === 2 && workflowType === 'video' && (
        <div className="space-y-4">
          {processedVideoUrl && (
            <div className="rounded-xl overflow-hidden border border-slate-700 bg-slate-950">
              <video
                key={processedVideoUrl}
                src={processedVideoUrl}
                className="w-full max-h-72 bg-black"
                controls
                muted
                loop
                playsInline
                poster={originalPreview || undefined}
              />
            </div>
          )}

          <div className="space-y-2">
            <h3 className="text-sm font-medium text-slate-300">Poster / thumbnail</h3>
            <p className="text-xs text-slate-500">
              Auto-generated from your video's first frame. This shows as the video preview and the social share image. Replace it if you'd prefer a different still.
            </p>
            {originalPreview && (
              <div className="rounded-xl overflow-hidden border border-slate-700 bg-slate-900">
                <img src={originalPreview} alt="Poster" className="w-full max-h-64 object-contain" />
              </div>
            )}
            <button
              onClick={() => document.getElementById('share-demo-poster')?.click()}
              className="text-sm text-slate-400 hover:text-white"
            >
              {originalPreview ? 'Replace poster image' : 'Upload poster image'}
            </button>
            <input
              id="share-demo-poster"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {processError && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl text-sm">
              {processError}
            </div>
          )}

          <div className="flex items-center gap-3">
            {originalFile && (
              <button
                onClick={handlePosterReplace}
                disabled={saving}
                className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
              >
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                {saving ? 'Saving...' : 'Save Poster'}
              </button>
            )}
            <button
              onClick={() => setStep(3)}
              disabled={saving || !processedUrl}
              className={`${originalFile ? 'px-6' : 'flex-1'} py-3 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-800/50 disabled:text-slate-500 text-white font-semibold rounded-xl transition-colors`}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2 (Hyper Dusk): Configure → Generate → Approve → Animate ── */}
      {step === 2 && workflowType === 'hyperdusk' && (
        <div className="space-y-5">
          {/* Configuration shown until we have a generated preview */}
          {!hdIsDuskReady && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Image Type</label>
                <div className="grid grid-cols-2 gap-3">
                  {HYPER_DUSK_SCENE_OPTIONS.map((opt) => {
                    const selected = opt.value === hdSceneType;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setHdSceneType(opt.value)}
                        className={`p-3 rounded-lg border text-left transition-colors ${
                          selected
                            ? 'border-indigo-500 bg-indigo-900/20'
                            : 'border-slate-700 bg-slate-900 hover:border-slate-600'
                        }`}
                      >
                        <p className="text-sm font-semibold text-white">{opt.label}</p>
                        <p className="text-xs text-slate-400 mt-1">{opt.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Dusk Style Preset</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {HYPER_DUSK_PRESETS.map((preset) => {
                    const selected = preset.id === hdPresetId;
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => {
                          setHdPresetId(preset.id);
                          setHdAnimationStyle(preset.defaultAnimationStyle);
                        }}
                        className={`p-3 rounded-lg border text-left transition-colors ${
                          selected
                            ? 'border-indigo-500 bg-indigo-900/20'
                            : 'border-slate-700 bg-slate-900 hover:border-slate-600'
                        }`}
                      >
                        <p className="text-sm font-semibold text-white">{preset.label}</p>
                        <p className="text-xs text-slate-400 mt-1">{preset.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {originalPreview && !hdIsDuskReady && (
            <div className="rounded-xl overflow-hidden border border-slate-700 bg-slate-900">
              <img src={originalPreview} alt="Original" className="w-full max-h-64 object-contain" />
            </div>
          )}

          {/* Generation in progress / preview ready */}
          {hdProjectId && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="rounded-lg border border-slate-700 bg-slate-950 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Original (Day)</p>
                <div className="aspect-video rounded bg-slate-900 flex items-center justify-center overflow-hidden">
                  {originalPreview && (
                    <img src={originalPreview} alt="Original" className="w-full h-full object-cover" />
                  )}
                </div>
              </div>
              <div className="rounded-lg border border-slate-700 bg-slate-950 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Night Version</p>
                <div className="aspect-video rounded bg-slate-900 flex items-center justify-center overflow-hidden">
                  {hdDuskPreview ? (
                    <img src={hdDuskPreview} alt="Generated night preview" className="w-full h-full object-cover" />
                  ) : hdGenerating || hdGenStatus === 'dusk_processing' ? (
                    <div className="flex items-center gap-2 text-amber-300 text-xs">
                      <Loader2 size={16} className="animate-spin" />
                      Generating night preview…
                    </div>
                  ) : (
                    <span className="text-xs text-slate-500">Waiting…</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Variant picker */}
          {hdVariants.length > 1 && (
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-slate-400">Saved Variants ({hdVariants.length})</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {hdVariants.map((variant, idx) => {
                  const isSelected = variant.id === hdSelectedVariant?.id;
                  return (
                    <button
                      key={variant.id}
                      type="button"
                      onClick={() => setHdSelectedVariantId(variant.id)}
                      className={`rounded-lg border p-1.5 text-left transition-colors ${
                        isSelected ? 'border-indigo-500 bg-indigo-900/20' : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'
                      }`}
                    >
                      <div className="aspect-video rounded overflow-hidden bg-slate-900">
                        {variant.url && <img src={variant.url} alt={`Variant ${idx + 1}`} className="w-full h-full object-cover" />}
                      </div>
                      <p className="text-[11px] text-slate-300 mt-1 font-medium">Variant {idx + 1}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Animation result */}
          {(hdActiveImage?.clip_url || hdProject?.final_video_url || processedVideoUrl) && (
            <div className="rounded-lg border border-slate-700 bg-slate-950 p-3">
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Time-lapse Animation</p>
              <video
                key={hdActiveImage?.clip_url || hdProject?.final_video_url || processedVideoUrl || ''}
                src={hdActiveImage?.clip_url || hdProject?.final_video_url || processedVideoUrl || ''}
                className="w-full rounded bg-black"
                controls
                muted
                loop
                playsInline
                autoPlay
              />
            </div>
          )}

          {processError && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl text-sm">
              {processError}
            </div>
          )}

          {hdActiveImage?.generation_error && hdGenStatus === 'failed' && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-300 px-4 py-3 rounded-xl text-xs space-y-2">
              <p className="font-semibold text-red-200">Hyper Dusk pipeline failed</p>
              <p className="font-mono break-words">{hdActiveImage.generation_error}</p>
              <p className="text-red-300/70">Click "Retry Animation" below — the manager will re-stage the original image and re-submit.</p>
            </div>
          )}

          {hdIsAnimationRunning && (
            <div className="flex items-center gap-2 text-xs text-amber-300">
              <Loader2 size={14} className="animate-spin" />
              AI animation is rendering. This usually takes 1–3 minutes — keep this tab open.
            </div>
          )}

          {hdSavingAssets && (
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Loader2 size={14} className="animate-spin" />
              Saving Hyper Dusk assets to share-demo storage…
            </div>
          )}

          {/* Action bar */}
          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-800 pt-4">
            {!hdIsDuskReady && hdGenStatus !== 'failed' && (
              <button
                type="button"
                onClick={handleHyperDuskGenerate}
                disabled={hdGenerating || !originalUrl}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 disabled:opacity-50 transition-colors"
              >
                {hdGenerating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {hdGenerating ? 'Starting…' : 'Generate Night Preview'}
              </button>
            )}

            {/* Recovery: image marked failed but a dusk preview already exists */}
            {hdGenStatus === 'failed' && hdDuskPreview && (
              <>
                <button
                  type="button"
                  onClick={handleHyperDuskGenerate}
                  disabled={hdGenerating}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
                >
                  {hdGenerating ? <Loader2 size={16} className="animate-spin" /> : <RefreshCcw size={16} />}
                  Regenerate Night
                </button>
                <button
                  type="button"
                  onClick={handleHyperDuskAnimate}
                  disabled={hdAnimating}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-500 disabled:opacity-50 transition-colors"
                >
                  {hdAnimating ? <Loader2 size={16} className="animate-spin" /> : <PlayCircle size={16} />}
                  Retry Animation
                </button>
              </>
            )}

            {hdIsDuskReady && !hdIsApproved && (
              <>
                <button
                  type="button"
                  onClick={handleHyperDuskGenerate}
                  disabled={hdGenerating}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
                >
                  {hdGenerating ? <Loader2 size={16} className="animate-spin" /> : <RefreshCcw size={16} />}
                  Regenerate
                </button>
                <button
                  type="button"
                  onClick={handleHyperDuskApprove}
                  disabled={hdApproving || !hdDuskPreview}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 disabled:opacity-50 transition-colors"
                >
                  {hdApproving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                  Approve Night Version
                </button>
              </>
            )}

            {hdIsApproved && !hdIsCompleted && (
              <>
                <div className="flex items-center gap-3 mr-auto">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-400">Duration</label>
                    <select
                      value={hdDuration}
                      onChange={(e) => setHdDuration(Number(e.target.value))}
                      className="px-2 py-1 text-xs border border-slate-700 rounded bg-slate-800 text-white"
                    >
                      {HYPER_DUSK_DURATION_OPTIONS.map((d) => (
                        <option key={d} value={d}>{d}s</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-400">Motion</label>
                    <select
                      value={hdAnimationStyle}
                      onChange={(e) => setHdAnimationStyle(e.target.value as HyperDuskAnimationStyle)}
                      className="px-2 py-1 text-xs border border-slate-700 rounded bg-slate-800 text-white"
                    >
                      {HYPER_DUSK_ANIMATION_STYLES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleHyperDuskAnimate}
                  disabled={hdAnimating || hdIsAnimationRunning}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-500 disabled:opacity-50 transition-colors"
                >
                  {hdAnimating || hdIsAnimationRunning ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <PlayCircle size={16} />
                  )}
                  {hdIsAnimationRunning ? 'Generating Animation…' : `Generate ${hdDuration}s Animation`}
                </button>
              </>
            )}

            {hdIsCompleted && processedVideoUrl && (
              <button
                type="button"
                onClick={() => setStep(3)}
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
              >
                Continue
                <ChevronRight size={16} />
              </button>
            )}
          </div>

          {hdGenerating && !hdIsDuskReady && (
            <p className="text-xs text-slate-500 text-center">
              Calling Gemini to generate the night version. This usually takes 10–20 seconds.
            </p>
          )}
        </div>
      )}

      {/* ── Step 3: Configure ──────────────────────────────── */}
      {step === 3 && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-300 mb-1">Public Headline</label>
              <input
                type="text"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white focus:outline-none focus:ring-1 focus:ring-orange-500"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-300 mb-1">Subheadline</label>
              <input
                type="text"
                value={subheadline}
                onChange={(e) => setSubheadline(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white focus:outline-none focus:ring-1 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Agency / Client Name</label>
              <input type="text" value={agencyName} onChange={(e) => setAgencyName(e.target.value)} placeholder="Optional" className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500" />
              <div className="mt-2 flex items-center gap-3">
                {agencyLogoUrl ? (
                  <>
                    <img src={agencyLogoUrl} alt="Client logo" className="h-9 w-auto max-w-[140px] object-contain bg-slate-950/60 rounded px-2 py-1 border border-slate-700" />
                    <button type="button" onClick={() => document.getElementById('agency-logo-input')?.click()} disabled={uploadingLogo} className="text-xs text-slate-400 hover:text-white disabled:opacity-50">
                      {uploadingLogo ? 'Uploading…' : 'Replace'}
                    </button>
                    <button type="button" onClick={removeAgencyLogo} className="text-xs text-slate-500 hover:text-red-400">Remove</button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => document.getElementById('agency-logo-input')?.click()}
                    disabled={!editingDemo || uploadingLogo}
                    title={!editingDemo ? 'Save the demo first, then add a logo' : undefined}
                    className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploadingLogo ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {uploadingLogo ? 'Uploading…' : 'Upload client logo'}
                  </button>
                )}
                <input id="agency-logo-input" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => { handleAgencyLogoSelect(e.target.files?.[0]); e.target.value = ''; }} />
              </div>
              <p className="text-[11px] text-slate-500 mt-1">Optional. Shown top-right of the public page instead of the name.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Source Label</label>
              <input type="text" value={sourceLabel} onChange={(e) => setSourceLabel(e.target.value)} placeholder='e.g. "Based on one of your live listings"' className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-300 mb-1">Listing URL</label>
              <input type="url" value={listingUrl} onChange={(e) => setListingUrl(e.target.value)} placeholder="https://… link to the live listing these images came from (optional)" className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500" />
              <p className="text-[11px] text-slate-500 mt-1">Shown as a "View the listing" button on the public page.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Slug</label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 shrink-0">{getShareDemoHost(workflowType)}/share/</span>
                <input type="text" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} className="flex-1 px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white focus:outline-none focus:ring-1 focus:ring-orange-500" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">CTA Mode</label>
              <select value={ctaMode} onChange={(e) => setCtaMode(e.target.value as ShareDemoCtaMode)} className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white focus:outline-none focus:ring-1 focus:ring-orange-500">
                <option value="none">No CTA</option>
                <option value="signup">Sign up (Try free)</option>
                <option value="demo">Book a Demo</option>
                <option value="both">Both</option>
              </select>
            </div>
            {(ctaMode === 'signup' || ctaMode === 'both') && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Signup Destination</label>
                  <select
                    value={SIGNUP_PRESETS.find((p) => p.url === ctaSignupUrl)?.id ?? 'custom'}
                    onChange={(e) => {
                      const preset = SIGNUP_PRESETS.find((p) => p.id === e.target.value);
                      if (preset) setCtaSignupUrl(preset.url);
                    }}
                    className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white focus:outline-none focus:ring-1 focus:ring-orange-500"
                  >
                    {SIGNUP_PRESETS.map((p) => (
                      <option key={p.id} value={p.id}>Try {p.label} Free</option>
                    ))}
                    <option value="custom">Custom URL…</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Signup URL</label>
                  <input type="text" value={ctaSignupUrl} onChange={(e) => setCtaSignupUrl(e.target.value)} className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white focus:outline-none focus:ring-1 focus:ring-orange-500" />
                </div>
              </>
            )}
            {(ctaMode === 'demo' || ctaMode === 'both') && (
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-1">Demo URL</label>
                <div className="flex items-stretch gap-2">
                  <input type="text" value={ctaDemoUrl} onChange={(e) => setCtaDemoUrl(e.target.value)} placeholder="https://calendly.com/… booking link for this agent" className="flex-1 px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500" />
                  <button
                    type="button"
                    onClick={saveCurrentAgentLink}
                    disabled={!ctaDemoUrl.trim()}
                    title="Save this booking link for reuse"
                    className="px-3 py-2.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed border border-slate-700 rounded-xl text-sm text-slate-200 whitespace-nowrap transition-colors"
                  >
                    Save link
                  </button>
                </div>
                {agentLinks.length > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    <select
                      value=""
                      onChange={(e) => { if (e.target.value) applyAgentLink(e.target.value); }}
                      className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-sm text-slate-300 focus:outline-none focus:ring-1 focus:ring-orange-500"
                    >
                      <option value="">Select a saved agent link…</option>
                      {agentLinks.map((l) => (
                        <option key={l.id} value={l.id}>{l.label}</option>
                      ))}
                    </select>
                    {agentLinks.some((l) => l.demoUrl === ctaDemoUrl.trim()) && (
                      <button
                        type="button"
                        onClick={() => {
                          const match = agentLinks.find((l) => l.demoUrl === ctaDemoUrl.trim());
                          if (match) deleteAgentLink(match.id);
                        }}
                        title="Remove this saved link"
                        className="p-2 text-slate-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                )}
                <p className="text-[11px] text-slate-500 mt-1">"Book a Demo" link. Saved links are stored in this browser for quick reuse across agents.</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Download Mode</label>
              <select value={downloadMode} onChange={(e) => setDownloadMode(e.target.value as ShareDemoDownloadMode)} className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white focus:outline-none focus:ring-1 focus:ring-orange-500">
                <option value="disabled">Disabled</option>
                <option value="free">Free Download</option>
                <option value="email_gate">Email Required</option>
              </select>
            </div>
          </div>

          {/* Toggles */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" checked={watermarkEnabled} onChange={(e) => setWatermarkEnabled(e.target.checked)} className="rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500" />
              Watermark on download
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" checked={brandingEnabled} onChange={(e) => setBrandingEnabled(e.target.checked)} className="rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500" />
              Show &quot;Powered by ReHub Studio&quot; footer
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Apps used to create this demo</label>
            <p className="text-[11px] text-slate-500 mb-2">
              Selected apps are credited on the public page ("Created with …") with their logo linking to the app.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SHARE_DEMO_APPS.map((app) => {
                const selected = appsUsed.includes(app.id);
                return (
                  <button
                    key={app.id}
                    type="button"
                    onClick={() =>
                      setAppsUsed((prev) =>
                        prev.includes(app.id) ? prev.filter((id) => id !== app.id) : [...prev, app.id]
                      )
                    }
                    className={`flex items-center gap-2 p-2.5 rounded-lg border text-left transition-colors ${
                      selected ? 'border-orange-500 bg-orange-900/20' : 'border-slate-700 bg-slate-900 hover:border-slate-600'
                    }`}
                  >
                    <div className="w-16 h-9 rounded bg-slate-950/60 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <img src={app.logo} alt="" className="max-w-full max-h-full object-contain" />
                    </div>
                    <span className="text-xs font-medium text-white truncate">{app.name}</span>
                    {selected && <Check size={14} className="text-orange-400 ml-auto flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Admin Notes</label>
            <textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} rows={2} placeholder="Internal notes (not shown publicly)" className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500 resize-none" />
          </div>

          <button
            onClick={handleSaveConfig}
            disabled={saving || !slug.trim()}
            className="w-full py-3 bg-orange-600 hover:bg-orange-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
          >
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
            {saving ? 'Saving...' : 'Save & Continue'}
          </button>
        </div>
      )}

      {/* ── Step 4: Publish & Analytics ────────────────────── */}
      {step === 4 && editingDemo && (
        <div className="space-y-6">
          {/* Preview */}
          {workflowType === 'tour' && tourUrl.trim() && (
            <div className="rounded-xl overflow-hidden border border-slate-800 bg-slate-950 aspect-video">
              <iframe
                key={tourUrl}
                src={tourUrl.trim()}
                title="360 tour preview"
                className="w-full h-full"
                allow="accelerometer; gyroscope; fullscreen; xr-spatial-tracking"
                allowFullScreen
              />
            </div>
          )}
          {workflowType !== 'video' && workflowType !== 'tour' && processedPreview && originalPreview && (
            <BeforeAfterSlider beforeUrl={originalPreview} afterUrl={processedPreview} />
          )}

          {(workflowType === 'hyperdusk' || workflowType === 'video') && processedVideoUrl && (
            <div className="rounded-xl overflow-hidden border border-slate-800 bg-slate-950">
              <video
                src={processedVideoUrl}
                className="w-full bg-black"
                controls
                muted
                loop
                playsInline
                poster={originalPreview || undefined}
              />
            </div>
          )}

          {/* Status + actions */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Status</p>
                <span className={`inline-block mt-1 px-3 py-1 text-xs font-medium rounded-full border ${STATUS_COLORS[status]}`}>
                  {status}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {status !== 'published' && canPublish && (
                  <button
                    onClick={async () => {
                      setStatus('published');
                      await updateShareDemo(editingDemo.id, {
                        status: 'published',
                        published_at: new Date().toISOString(),
                      } as any);
                      setEditingDemo((prev) => prev ? { ...prev, status: 'published' } : prev);
                    }}
                    className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Publish
                  </button>
                )}
                {status === 'published' && (
                  <button
                    onClick={async () => {
                      setStatus('unpublished');
                      await updateShareDemo(editingDemo.id, { status: 'unpublished' });
                      setEditingDemo((prev) => prev ? { ...prev, status: 'unpublished' } : prev);
                    }}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Unpublish
                  </button>
                )}
              </div>
            </div>

            {/* Link */}
            <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-4 py-2.5">
              <span className="text-sm text-slate-400 truncate flex-1">{getShareDemoUrl(editingDemo.workflow_type, editingDemo.slug)}</span>
              <button
                onClick={() => copyLink(editingDemo.slug, editingDemo.workflow_type)}
                className="p-1.5 text-slate-400 hover:text-white transition-colors"
              >
                {copiedId === editingDemo.slug ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
              </button>
              <a
                href={getShareDemoUrl(editingDemo.workflow_type, editingDemo.slug)}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 text-slate-400 hover:text-white transition-colors"
              >
                <ExternalLink size={16} />
              </a>
            </div>
          </div>

          {/* Analytics */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-white">Analytics</h3>
            <ShareDemoAnalytics demoId={editingDemo.id} />
          </div>
        </div>
      )}
    </div>
  );
}
